import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Settings, 
    User, 
    Shield, 
    Users, 
    Plus,
    Trash2,
    Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    { value: 'local_controller', label: 'Local Controller' },
    { value: 'deputy_local_controller', label: 'Deputy Local Controller' },
    { value: 'unit_duty_officer', label: 'Unit Duty Officer' },
    { value: 'group_leader', label: 'Group Leader' },
    { value: 'deputy_group_leader', label: 'Deputy Group Leader' },
    { value: 'general_member', label: 'General Member' },
];

export default function SettingsPage() {
    const { user, updateUser, hasUnitAccess, logout } = useAuth();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showAddGroup, setShowAddGroup] = useState(false);
    const [newGroup, setNewGroup] = useState({ name: '', description: '' });
    
    const [profileData, setProfileData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        emergency_contact_name: '',
        emergency_contact_phone: ''
    });
    
    useEffect(() => {
        if (user) {
            setProfileData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || '',
                emergency_contact_name: user.emergency_contact_name || '',
                emergency_contact_phone: user.emergency_contact_phone || ''
            });
        }
        fetchGroups();
    }, [user]);
    
    const fetchGroups = async () => {
        try {
            const response = await axios.get(`${API_URL}/groups`);
            setGroups(response.data);
        } catch (error) {
            console.error('Failed to fetch groups:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            const response = await axios.put(`${API_URL}/users/${user.id}`, profileData);
            updateUser(response.data);
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };
    
    const handleAddGroup = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/groups`, newGroup);
            toast.success('Group created');
            setShowAddGroup(false);
            setNewGroup({ name: '', description: '' });
            fetchGroups();
        } catch (error) {
            toast.error('Failed to create group');
        }
    };
    
    const handleDeleteGroup = async (groupId) => {
        if (!window.confirm('Are you sure you want to delete this group?')) return;
        try {
            await axios.delete(`${API_URL}/groups/${groupId}`);
            toast.success('Group deleted');
            fetchGroups();
        } catch (error) {
            toast.error('Failed to delete group');
        }
    };
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-8 w-32 rounded"></div>
                <Card>
                    <CardContent className="p-6">
                        <div className="skeleton h-48 rounded"></div>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="settings-page">
            <div>
                <h2 className="text-2xl font-bold">Settings</h2>
                <p className="text-muted-foreground">
                    Manage your account and unit settings
                </p>
            </div>
            
            <Tabs defaultValue="profile" className="w-full">
                <TabsList>
                    <TabsTrigger value="profile" data-testid="tab-profile">
                        <User className="w-4 h-4 mr-2" />
                        Profile
                    </TabsTrigger>
                    {hasUnitAccess() && (
                        <TabsTrigger value="groups" data-testid="tab-groups">
                            <Users className="w-4 h-4 mr-2" />
                            Groups
                        </TabsTrigger>
                    )}
                </TabsList>
                
                {/* Profile Tab */}
                <TabsContent value="profile" className="mt-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Profile Information</CardTitle>
                            <CardDescription>Update your personal details</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Avatar Section */}
                            <div className="flex items-center gap-6">
                                <Avatar className="h-20 w-20">
                                    <AvatarImage src={user?.profile_image} />
                                    <AvatarFallback className="bg-primary text-white text-2xl">
                                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <h3 className="font-semibold text-lg">
                                        {user?.first_name} {user?.last_name}
                                    </h3>
                                    <Badge className="mt-1">{user?.role_name}</Badge>
                                </div>
                            </div>
                            
                            <Separator />
                            
                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>First Name</Label>
                                    <Input
                                        value={profileData.first_name}
                                        onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                                        data-testid="profile-firstname"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Last Name</Label>
                                    <Input
                                        value={profileData.last_name}
                                        onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                                        data-testid="profile-lastname"
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={profileData.email}
                                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                                        data-testid="profile-email"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Phone</Label>
                                    <Input
                                        value={profileData.phone}
                                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                        data-testid="profile-phone"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Address</Label>
                                <Input
                                    value={profileData.address}
                                    onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                                    data-testid="profile-address"
                                />
                            </div>
                            
                            <Separator />
                            
                            {/* Emergency Contact */}
                            <div>
                                <h4 className="font-medium mb-4 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-red-500" />
                                    Emergency Contact
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Contact Name</Label>
                                        <Input
                                            value={profileData.emergency_contact_name}
                                            onChange={(e) => setProfileData({ ...profileData, emergency_contact_name: e.target.value })}
                                            data-testid="profile-emergency-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Contact Phone</Label>
                                        <Input
                                            value={profileData.emergency_contact_phone}
                                            onChange={(e) => setProfileData({ ...profileData, emergency_contact_phone: e.target.value })}
                                            data-testid="profile-emergency-phone"
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-3">
                                <Button
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                    className="ses-btn-primary gap-2"
                                    data-testid="save-profile"
                                >
                                    <Save className="w-4 h-4" />
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Account Actions */}
                    <Card className="mt-6">
                        <CardHeader>
                            <CardTitle>Account</CardTitle>
                            <CardDescription>Manage your account settings</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Button variant="destructive" onClick={logout} data-testid="logout-btn">
                                Log Out
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Groups Tab */}
                {hasUnitAccess() && (
                    <TabsContent value="groups" className="mt-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Groups</CardTitle>
                                        <CardDescription>Manage unit groups and teams</CardDescription>
                                    </div>
                                    <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
                                        <DialogTrigger asChild>
                                            <Button className="ses-btn-primary gap-2" data-testid="add-group-btn">
                                                <Plus className="w-4 h-4" />
                                                Add Group
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Create Group</DialogTitle>
                                                <DialogDescription>
                                                    Add a new group to the unit
                                                </DialogDescription>
                                            </DialogHeader>
                                            <form onSubmit={handleAddGroup} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Group Name</Label>
                                                    <Input
                                                        value={newGroup.name}
                                                        onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                                                        placeholder="e.g., Storm Team Alpha"
                                                        required
                                                        data-testid="group-name"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Description</Label>
                                                    <Input
                                                        value={newGroup.description}
                                                        onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                                                        placeholder="Group description (optional)"
                                                        data-testid="group-description"
                                                    />
                                                </div>
                                                <DialogFooter>
                                                    <Button type="button" variant="outline" onClick={() => setShowAddGroup(false)}>
                                                        Cancel
                                                    </Button>
                                                    <Button type="submit" className="ses-btn-primary" data-testid="submit-group">
                                                        Create Group
                                                    </Button>
                                                </DialogFooter>
                                            </form>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {groups.length > 0 ? (
                                    <div className="space-y-3">
                                        {groups.map((group) => (
                                            <div
                                                key={group.id}
                                                className="flex items-center justify-between p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="p-2 rounded-lg bg-primary/10">
                                                        <Users className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium">{group.name}</h4>
                                                        {group.description && (
                                                            <p className="text-sm text-muted-foreground">{group.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Badge variant="secondary">
                                                        {group.member_count} members
                                                    </Badge>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-destructive hover:text-destructive"
                                                        onClick={() => handleDeleteGroup(group.id)}
                                                        data-testid={`delete-group-${group.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>No groups created yet</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
